import { boolean } from "boolean";
import { useMSAL } from "./msal";
import type { AuthTypes } from "~/types/auth";
import { useRequest } from "~/composables/request";
import type { UserAuth } from "~/types/entities/auth";

export function useAuth() {
  const {
    login: msalLogin,
    logout: msalLogout,
    getAccounts,
    getToken,
    currentAccount,
    msalToken,
  } = useMSAL();
  const authType = ref<AuthTypes>("azureAD");
  const sessionStore = useSessionStore();
  const runtimeConfig = useRuntimeConfig();

  const { user } = storeToRefs(sessionStore);
  const requestErrorHandler = useRequestErrorHandler();

  async function login<T extends object>(requestData: { payload?: T }) {
    if (!authType.value) return;

    if (authType.value === "password") {
      sessionStore.processingAuth = true;
      const { payload } = requestData;
      try {
        const response = await useRequest<{ data: UserAuth }>("auth/login", {
          body: payload,
          method: "POST",
        });
        if (response._data?.data) sessionStore.user = response._data.data;
        await navigateTo({ name: "index" });
      } catch (error) {
        requestErrorHandler({
          error,
          rethrow: false,
        });
      }
      sessionStore.processingAuth = false;
    }

    if (authType.value === "azureAD") {
      msalLogin();
    }
  }

  async function logout(logoutFromServer = true) {
    if (logoutFromServer) {
      await Promise.allSettled([
        boolean(runtimeConfig.public.enableSocialAuth)
          ? msalLogout()
          : Promise.resolve(),
        useRequest("auth/logout"),
      ]);
    }

    await sessionStore.resetUserData();
    await navigateTo({ name: "login" });
    if (import.meta.client) window.location.reload();
  }

  function setToken(token: string) {
    user.value.accessToken = token;
  }

  async function changePassword(payload: { token: string; password: string }) {
    await useRequest("auth/password/reset", {
      body: payload,
      method: "POST",
    });
  }

  async function requestPasswordResetToken(email: string) {
    await useRequest("auth/password/forgot", {
      body: {
        email,
      },
      method: "POST",
    });
  }

  async function syncAzureAuth() {
    await isModuleReady(sessionStore);
    await getAccounts();
    if (currentAccount.value)
      await getToken(
        { scopes: ["user.read"] },
        { loginHint: currentAccount.value?.username },
      );
    if (currentAccount.value?.idToken && msalToken.value)
      await authenticateWithMicrosoftToken(msalToken.value);
  }

  async function authenticateWithMicrosoftToken(token: string) {
    sessionStore.processingAuth = true;
    try {
      const response = await useRequest<{ data: UserAuth }>(
        "auth/login/microsoft",
        {
          method: "POST",
          body: {
            token,
          },
          timeout: 60000,
        },
      );
      if (response._data?.data.accessToken) {
        setToken(response._data.data.accessToken);
        sessionStore.user = response._data.data;
      }
    } catch {
      //
    }
    sessionStore.processingAuth = false;
  }

  return {
    authType,
    login,
    logout,
    setToken,
    changePassword,
    requestPasswordResetToken,
    syncAzureAuth,
  };
}
